import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { DialogConfig, DialogService } from "@obg/mat.ui.core";
import {
	CORE_ROUTE,
	CoreState,
	DepositLimitConfigService,
	DepositLimitRequiredService,
	DialogStatusSelector,
	Jurisdiction,
	JurisdictionSelector,
} from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, Observable, of } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { SgaSessionLimitRequiredGuard } from "../../session-limit/sga/sga-session-limit-required.guard";

import { MultiDepositLimitDialogContainer } from "./multi-deposit-limit-dialog/multi-deposit-limit-dialog.container";

@Injectable({
	providedIn: "root",
})
export class MultiDepositLimitRequiredGuard implements CanActivate {
	private readonly mobileDialogConfig: Partial<DialogConfig> = {
		panelClass: ["obg-m-multi-deposit-limit-dialog", "obg-m-alert-extra-wide"],
		closeOnNavigation: true,
		disableClose: true,
		width: "100%",
		height: "100%",
		maxWidth: "none",
		maxHeight: "none",
	};

	constructor(
		private depositLimitConfig: DepositLimitConfigService,
		private depositLimitRequiredService: DepositLimitRequiredService,
		private dialogSelector: DialogStatusSelector,
		private dialogService: DialogService,
		private store: Store<CoreState>,
		private sessionLimitGuard: SgaSessionLimitRequiredGuard,
		private deviceConfigService: DeviceConfigService
	) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		if (!this.depositLimitConfig.getIsDepositLimitRequired()) {
			return of(true);
		}

		return this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			switchMap((jurisdiction) =>
				jurisdiction !== Jurisdiction.Sga
					? of(true)
					: this.depositLimitRequiredService.shouldOpenDepositLimit$().pipe(
							switchMap((shouldRedirect) =>
								!shouldRedirect
									? this.sessionLimitGuard.canActivate(route, state)
									: combineLatest([
											this.store.select(RouteSelector.getCurrent),
											this.store.pipe(select(this.dialogSelector.hasOpenDialogs())),
										]).pipe(
											filter(([, hasOpenDialogs]) => !hasOpenDialogs),
											tap(([currentRoute]) => {
												const fallbackLobby = route.data?.product || CORE_ROUTE.home;
												if (this.deviceConfigService.isDesktop()) {
													this.store.dispatch(
														RouteAction.navigate(
															!currentRoute
																? fallbackLobby
																: this.depositLimitConfig.isSgaDepositLimitDialogEnabled()
																	? CORE_ROUTE.responsibleGaming.depositLimitDialog
																	: CORE_ROUTE.responsibleGaming.depositLimit,
															undefined,
															{ queryParams: { returnUrl: state.url } }
														)
													);
													return;
												}

												this.dialogService
													.openComponent(
														MultiDepositLimitDialogContainer,
														this.mobileDialogConfig
													)
													.beforeClosed()
													.pipe(
														switchMap(() =>
															this.depositLimitRequiredService.isDepositLimitUnset$()
														),
														tap((isUnset) => {
															if (!isUnset) {
																this.store.dispatch(
																	RouteAction.navigateByUrl(state.url)
																);
																return;
															}

															if (
																!currentRoute ||
																currentRoute?.name === CORE_ROUTE.auth.login
															) {
																this.store.dispatch(
																	RouteAction.navigate(fallbackLobby)
																);
															}
														}),
														take(1)
													)
													.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
											}),
											map(() => false),
											take(1)
										)
							)
						)
			)
		);
	}
}
